/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import axios from "../api/axiosInstance";
import {
  reloadAdultsFromDB,
  reloadChildrenFromDB,
  reloadDatesFromDB,
  reloadRoomCategoryIdFromDB,
  reloadRoomRateIdFromDB,
} from "../utils/reloaders";

/**
 * Hook para copiar datos de una reserva a otra y refrescar estados del frontend.
 */
export const useCopyReservationInfo = ({
  setAdultsMap,
  setChildrenMap,
  setDbDatesMap,
  setRoomCategoryMap,
  setRoomRateDetailsMap,
  setHasAnyDates,
  setCopyFromOptions,
  setCopyToOptions,
  setShowCopyPanel,
  setCopyFrom,
  setCopyTo,
  formData,
  copyFrom,
  copyTo,
  copyToOptions,
}) => {
  const handleCopyReservationInfo = async () => {
  if (!copyFrom || !copyTo) {
    alert("Please select both source and target rooms.");
    return;
  }

  const targets = copyTo === "all"
    ? copyToOptions.map(opt => opt.id).filter(id => id !== parseInt(copyFrom))
    : [parseInt(copyTo)];

  let successCount = 0;
  let failCount = 0;
  let errorMessages = [];

  try {
    for (const toId of targets) {
      try {
        const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/reservations/copy/${copyFrom}/to/${toId}`);
        await reloadAdultsFromDB(toId, setAdultsMap);
        await reloadChildrenFromDB(toId, setChildrenMap);
        await reloadDatesFromDB(toId, setDbDatesMap);
        await reloadRoomCategoryIdFromDB(toId, setRoomCategoryMap);
        await reloadRoomRateIdFromDB(toId, setRoomRateDetailsMap);

        // Ya no alertes aquí
        successCount++;
        console.log(`✅ Datos copiados de ${copyFrom} a ${toId}`);
      } catch (error) {
        const msg = error.response?.data?.error || "Unknown error while copying reservation.";
        failCount++;
        errorMessages.push(`ID ${toId}: ${msg}`);
        console.error("❌ Copy error:", error);
      }
    }

    // Muestra el resumen al final:
    let summary = `${successCount} reservations copied successfully.`;
    if (failCount > 0) {
      summary += `\n${failCount} failed:\n- ` + errorMessages.join("\n- ");
    }
    alert(summary);

    // Reset UI solo si todo se ejecuta sin errores fatales
    setShowCopyPanel(false);
    setCopyFrom("");
    setCopyTo("");

    if (formData.reservation_group_id) {
      const copyRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/reservation-groups/${formData.reservation_group_id}/copy-info-options`
      );
      setHasAnyDates(copyRes.data.has_any_dates);
      setCopyFromOptions(copyRes.data.copy_from_options);
      setCopyToOptions(copyRes.data.copy_to_options);
    }
  } catch (error) {
    console.error("❌ General error during copy loop:", error);
    alert("Unexpected error while copying reservations.");
  }
};


  return { handleCopyReservationInfo };
};
